import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IVec3 } from '@stair-craft/common/util';

import { balusterCount } from '../shared-baluster-count/baluster-count';
import { ISpiralLayout } from '../spiral-calculate-layout/calculate-layout';

/** Circular guard rail around the upper-floor opening, with a 90° gap at the exit landing. */
export function generateUpperFloorRailing(config: IStairConfig, layout: ISpiralLayout): IRailingGeometry[] {
    const rc: IRailingConfig = config.railing;
    const y: number = config.floorHeight + config.floorThickness;
    const oR: number = layout.outerRadius;
    const cx: number = oR;
    const cz: number = oR;

    // Arc follows the opening edge on the slab: the posts' inner faces are flush with the hole.
    const openingR: number = Math.min(config.openingWidth, config.openingLength) / 2;
    const arcR: number = openingR + rc.postWidth / 2;

    const clockwise: boolean = config.turnDirection === 'right';
    const dirSign: number = clockwise ? 1 : -1;
    const totalAngleRad: number = (layout.totalRotationDeg * Math.PI) / 180;
    const exitAngle: number = totalAngleRad * dirSign;
    const exitGapAngle: number = Math.PI / 2;

    // ── 1. Arc railing (270° around opening, gap at exit landing) ──
    const arcStart: number = exitAngle + exitGapAngle * dirSign;
    const arcEnd: number = exitAngle + 2 * Math.PI * dirSign;
    const arcSpan: number = arcEnd - arcStart;
    const arcLength: number = arcR * Math.abs(arcSpan);
    const ptCount: number = Math.max(24, Math.floor(arcLength / 100));
    const balCount: number = balusterCount(arcLength, rc, 4);

    const arcPosts: IBalusterGeometry[] = [];
    const arcBalusters: IBalusterGeometry[] = [];
    const arcHandrail: IVec3[] = [];

    arcPosts.push({
        position: { y, x: cx + arcR * Math.sin(arcStart), z: cz + arcR * Math.cos(arcStart) },
        height: rc.height,
        rotation: 0,
    });
    arcPosts.push({
        position: { y, x: cx + arcR * Math.sin(arcEnd), z: cz + arcR * Math.cos(arcEnd) },
        height: rc.height,
        rotation: 0,
    });

    for (let i: number = 0; i <= ptCount; i++) {
        const angle: number = arcStart + (arcSpan * i) / ptCount;
        arcHandrail.push({ x: cx + arcR * Math.sin(angle), y: y + rc.height, z: cz + arcR * Math.cos(angle) });
    }
    for (let i: number = 1; i < balCount; i++) {
        const angle: number = arcStart + (arcSpan * i) / balCount;
        arcBalusters.push({
            position: { y, x: cx + arcR * Math.sin(angle), z: cz + arcR * Math.cos(angle) },
            height: rc.height,
            rotation: 0,
        });
    }

    return [
        {
            side: 'left',
            balusters: arcBalusters,
            handrail: { points: arcHandrail, width: rc.handrailWidth, height: rc.handrailHeight },
            posts: arcPosts,
            balusterWidth: rc.balusterWidth,
            postWidth: rc.postWidth,
        },
    ];
}
